import Fuse from 'fuse.js';
import { onReady } from './util/onReady';

// Cache items once to avoid querying the DOM on every keystroke
interface CategoryCacheItem {
    categoryElement: HTMLLIElement;
    items: { el: HTMLLIElement; text: string; synonyms: string[] }[];
}

// Each inner array is a synonym group — all terms are interchangeable when searching
const SYNONYMS: string[][] = [
    ['aksjer', 'stock market', 'børs', 'stock exchange', 'aksjemarked'],
    ['balansert målstyring', 'balanced scorecard', 'bsc'],
    ['bedriftsøkonomi', 'foretaksøkonomi'],
    ['big data', 'dataplattform', 'data warehouse', 'datavarehus'],
    ['bærekraft', 'sustainability', 'grønn omstilling', 'green transition'],
    ['corona', 'covid-19', 'covid', 'pandemi', 'pandemic'],
    ['customer relationship management', 'crm', 'kundehåndtering'],
    ['dataanalyse', 'business analytics', 'marketing analytics'],
    ['digitalisering', 'digital transformation'],
    ['eierstyring', 'bedriftsstyring', 'corporate governance', 'selskapsstyring', 'virksomhetsstyring'],
    ['entreprenørskap', 'gründere', 'startup', 'oppstart'],
    ['esg', 'environmental social governance', 'ansvarlige investeringer'],
    ['etikk', 'forretningsetikk', 'ethics'],
    ['familieøkonomi', 'privatøkonomi', 'household finance'],
    ['finansregnskap', 'regnskap'],
    ['flyselskaper', 'luftfart'],
    ['handel', 'varehandel'],
    ['helse', 'wellbeing', 'velvære', 'utbrenthet', 'burnout', 'stressmestring', 'stress'],
    [
        'psykologi',
        'psykologisk trygghet',
        'psychological safety',
        'arbeidsglede',
        'lykke',
        'motivasjon',
        'selvledelse',
        'organisasjonspsykologi',
        'mindfulness',
    ],
    ['human resource management', 'hrm', 'personalledelse', 'hr'],
    ['intellectual property rights', 'ipr', 'immaterielle rettigheter', 'patent', 'copyright', 'opphavsrett'],
    ['internasjonal handel', 'international trade'],
    ['internasjonal politikk', 'international politics', 'geopolitikk', 'geopolitics'],
    ['klima', 'global oppvarming', 'climate', 'climate change'],
    ['konflikt', 'krig', 'conflict', 'war', 'terror', 'terrorism', 'terrorisme'],
    ['kunstig intelligens', 'ai', 'ki', 'artificial intelligence', 'maskinlæring', 'machine learning', 'ml'],
    ['merkevarer', 'brand management', 'varemerke', 'branding'],
    ['netthandel', 'e-commerce', 'e-handel'],
    ['oppkjøp og fusjoner', 'mergers and acquisitions', 'm&a', 'fusjoner og oppkjøp'],
    ['organisasjonskulturer', 'bedriftskultur'],
    ['pengepolitikk', 'renter', 'styringsrenten'],
    ['personvern', 'gdpr', 'databeskyttelse', 'data protection', 'privacy'],
    ['portefølje- og portfoliostyring', 'portfolio management', 'kapitalforvaltning', 'asset management'],
    ['rekruttering', 'talent acquisition', 'bemanning'],
    ['revisjon', 'audit'],
    ['samfunnsansvar', 'corporate social responsibility', 'csr'],
    ['selskapsrett', 'company law', 'corporate law'],
    ['kundeservice', 'customer service'],
    ['sosiale medier', 'social media', 'some', 'facebook', 'instagram', 'linkedin', 'tiktok', 'twitter'],
    ['teamarbeid', 'virtuelle team', 'teamwork', 'gruppearbeid'],
    ['utenlandsk valuta', 'valuta', 'fx', 'forex', 'foreign exchange', 'euro', 'dollar', 'pund', 'yen', 'kroner'],
    ['økonomistyring'],
];

const getSynonyms = (text: string): string[] => {
    const lower = text.toLowerCase();
    // Use word-boundary check to prevent e.g. 'handel' matching inside compound words like 'netthandel' (only for synonyms)
    return SYNONYMS.filter((category) =>
        category.some((term) => {
            const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return new RegExp(`(?:^|[^a-zæøå])${escaped}(?:[^a-zæøå]|$)`).test(lower);
        }),
    ).flat();
};

const updateStickyOffsets = (container: HTMLElement, stickyHeader: HTMLElement | null) => {
    const height = stickyHeader ? stickyHeader.offsetHeight : 0;
    container.style.setProperty('--sticky-header-height', `${height}px`);
};

const initExpertAreasSearch = () => {
    const searchInput = document.getElementById('expert-areas-search-input') as HTMLInputElement | null;
    const stickyHeader = document.getElementById('expert-areas-sticky-header');
    const list = document.querySelector('.expert-areas-list');
    const container = document.getElementById('expert-areas');

    if (!searchInput || !list || !container) return;

    // Build search cache upfront
    const categories: CategoryCacheItem[] = Array.from(list.querySelectorAll<HTMLLIElement>(':scope > li')).map(
        (category) => ({
            categoryElement: category,
            items: Array.from(category.querySelectorAll<HTMLLIElement>('.expert-areas-group > li')).map((el) => {
                const text = el.textContent?.trim() ?? '';
                return { el, text, synonyms: getSynonyms(text) };
            }),
        }),
    );

    const allItems = categories.flatMap((g) => g.items);

    const fuse = new Fuse(allItems, {
        keys: [
            { name: 'text', weight: 2 },
            { name: 'synonyms', weight: 1 },
        ],
        threshold: 0.2, // 0 = perfect match only, 1 = match anything; 0.2 allows ~1 typo on short words. (10++ = 2 typos, 0-10 characters = 1 typo)
        ignoreLocation: true, //remove position bias, so that "finans" matches "økonomi og finans"
        ignoreDiacritics: true, // ignore accents, so that "økonomi" matches "okonomi"
        minMatchCharLength: 2, // only match if at least 2 characters match
        includeScore: false, // we don't need the score, just the matches
    });

    const applyFilter = () => {
        const query = searchInput.value.trim();

        if (!query) {
            categories.forEach(({ categoryElement, items }) => {
                categoryElement.hidden = false;
                items.forEach(({ el }) => (el.hidden = false));
            });
        } else {
            const matched = new Set(fuse.search(query).map((r) => r.item.el));

            categories.forEach(({ categoryElement, items }) => {
                let categoryHasMatch = false;
                items.forEach(({ el }) => {
                    el.hidden = !matched.has(el);
                    if (!el.hidden) categoryHasMatch = true;
                });
                categoryElement.hidden = !categoryHasMatch;
            });
        }

        updateStickyOffsets(container, stickyHeader);
    };

    const SESSION_KEY = 'expert-areas-search-query';

    searchInput.addEventListener('input', () => {
        sessionStorage.setItem(SESSION_KEY, searchInput.value);
        applyFilter();
    });

    // Restore last search query from sessionStorage (survives back-navigation)
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) searchInput.value = saved;

    applyFilter();

    // The 'reset' event fires before the form actually clears the input's value,
    // so defer applyFilter until the browser has reset it
    const searchForm = document.getElementById('expert-areas-search-form');
    searchForm?.addEventListener('reset', () =>
        requestAnimationFrame(() => {
            sessionStorage.removeItem(SESSION_KEY);
            applyFilter();
        }),
    );

    // Handle initial resize
    window.addEventListener('resize', () => updateStickyOffsets(container, stickyHeader));
    updateStickyOffsets(container, stickyHeader);
};

onReady(() => {
    initExpertAreasSearch();

    // Native hash navigation adjustment
    if (window.location.hash) {
        requestAnimationFrame(() => {
            const target = document.querySelector<HTMLElement>(window.location.hash);
            target?.scrollIntoView({ behavior: 'smooth' });
        });
    }
});
